import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { RawTrade } from '../function/type';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler
);

interface ModalPnLChartProps {
  trades?: RawTrade[];
  data?: number[];
  labels?: (string | number)[];
}

const ModalPnLChart: React.FC<ModalPnLChartProps> = ({ 
  trades = [],
  data = [0],
  labels = ['0']
}) => {
  // 1. Sắp xếp danh sách lệnh theo thời gian tăng dần
  const sortedTrades = useMemo(() => {
    if (!trades || trades.length === 0) return [];
    return [...trades].sort((a, b) => {
      const tA = new Date(a.close_time || a.open_time || 0).getTime();
      const tB = new Date(b.close_time || b.open_time || 0).getTime();
      return tA - tB;
    });
  }, [trades]);

  // 2. Tính PnL tích lũy & nhãn thời gian cho từng điểm dữ liệu
  const { chartDataPoints, chartTimeLabels } = useMemo(() => {
    if (sortedTrades.length === 0) {
      return { chartDataPoints: data, chartTimeLabels: labels };
    }

    let cumulativePnL = 0;
    const chartDataPoints = [0]; // Điểm mốc ban đầu (0$)
    const chartTimeLabels: (string | number)[] = ['Bắt đầu'];

    sortedTrades.forEach((trade, idx) => {
      cumulativePnL += (trade.net_profit || 0);
      chartDataPoints.push(Number(cumulativePnL.toFixed(2)));

      const timeStr = trade.close_time
        ? new Date(trade.close_time).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
        : `#${idx + 1}`;
      chartTimeLabels.push(timeStr);
    });

    return { chartDataPoints, chartTimeLabels };
  }, [sortedTrades, data, labels]);

  // 3. Cấu hình Chart.js Data
  const chartData = {
    labels: chartTimeLabels,
    datasets: [
      {
        label: 'PnL Tích lũy',
        data: chartDataPoints,
        borderColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea, scales } = chart;
          if (!chartArea || !scales.y) return '#14b8a6';

          // Tự động tính điểm chuyển màu chính xác tại đường 0 (Zero Line)
          const zeroY = scales.y.getPixelForValue(0);
          const topY = chartArea.top;
          const bottomY = chartArea.bottom;
          const zeroRatio = Math.max(0, Math.min(1, (bottomY - zeroY) / (bottomY - topY)));

          const gradient = ctx.createLinearGradient(0, bottomY, 0, topY);
          gradient.addColorStop(0, '#f43f5e'); // Đỏ cho vùng âm
          gradient.addColorStop(zeroRatio, '#f43f5e');
          gradient.addColorStop(zeroRatio, '#14b8a6'); // Xanh cho vùng dương
          gradient.addColorStop(1, '#14b8a6');
          return gradient;
        },
        borderWidth: 2,
        fill: true,
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea, scales } = chart;
          if (!chartArea || !scales.y) return 'rgba(20, 184, 166, 0.2)';

          const zeroY = scales.y.getPixelForValue(0);
          const topY = chartArea.top;
          const bottomY = chartArea.bottom;
          const zeroRatio = Math.max(0, Math.min(1, (bottomY - zeroY) / (bottomY - topY)));

          const gradient = ctx.createLinearGradient(0, bottomY, 0, topY);
          gradient.addColorStop(0, 'rgba(244, 63, 94, 0.25)');
          gradient.addColorStop(zeroRatio, 'rgba(244, 63, 94, 0)');
          gradient.addColorStop(zeroRatio, 'rgba(20, 184, 166, 0)');
          gradient.addColorStop(1, 'rgba(20, 184, 166, 0.25)');
          return gradient;
        },
        tension: 0.2,
        pointRadius: chartDataPoints.length > 20 ? 0 : 3,
        pointHoverRadius: 6,
        pointBackgroundColor: chartDataPoints.map(val => val >= 0 ? '#14b8a6' : '#f43f5e'),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        titleFont: { family: 'Inter', size: 12 },
        bodyFont: { family: 'JetBrains Mono', size: 13, weight: 'bold' },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => {
            const val = context.parsed.y;
            return ` PnL Tích lũy: ${val >= 0 ? '$' + val.toFixed(2) : '-$' + Math.abs(val).toFixed(2)}`;
          }
        }
      },
    },
    scales: {
      x: { 
        grid: { display: false },
        ticks: {
          font: { family: 'JetBrains Mono', size: 10 },
          color: '#94a3b8',
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8
        }
      },
      y: {
        grid: { color: '#f1f5f9', borderDash: [4, 4] },
        ticks: {
          font: { family: 'JetBrains Mono', size: 10 },
          color: '#94a3b8',
          callback: (value: any) => `${value >= 0 ? '$' + value : '-$' + Math.abs(value)}`
        },
      },
    },
  };

  return <Line data={chartData} options={options as any} />;
};

export default ModalPnLChart;